/**
 * Base API client configuration
 *
 * This module provides a configured fetch wrapper for making API requests
 * with consistent error handling, headers, and request/response interceptors.
 */

interface ApiRequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  unwrapData?: boolean;
  isFormData?: boolean; // Skip Content-Type header for FormData
}

interface ApiError {
  message: string;
  statusCode: number;
  code?: string;
  details?: unknown;
}

/**
 * Base API path - should be configured via environment variable
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Auth error callback - set by AuthProvider
 */
let onAuthErrorCallback: (() => void) | null = null;

/**
 * Set the callback to be called when auth fails
 */
export function setAuthErrorCallback(callback: () => void) {
  onAuthErrorCallback = callback;
}

/**
 * Build query string from params object
 */
function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

/**
 * Resolve API base URL for the URL constructor.
 * Relative bases (e.g. `/api`) are invalid as `new URL()` bases and must be
 * resolved against the current page origin in the browser.
 */
function resolveApiBase(baseUrl: string): string {
  if (/^https?:\/\//i.test(baseUrl)) {
    return baseUrl;
  }
  if (typeof window !== 'undefined') {
    return new URL(baseUrl, window.location.origin).href;
  }
  return baseUrl;
}

function buildApiUrl(
  path: string,
  baseUrl: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  const resolvedBase = resolveApiBase(baseUrl);
  const url = new URL(
    path,
    resolvedBase.endsWith('/') ? resolvedBase : `${resolvedBase}/`,
  );
  if (params) {
    const queryString = buildQueryString(params);
    if (queryString) {
      url.search = queryString;
    }
  }
  return url.toString();
}

/**
 * API client class with methods for common HTTP operations
 */
class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get authentication token from storage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Build full URL for API requests
   */
  private buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
    return buildApiUrl(path, this.baseUrl, params);
  }

  /**
   * Build default headers
   * When isFormData is true, skip Content-Type to let browser set it with boundary
   */
  private buildHeaders(isFormData = false): HeadersInit {
    const headers: HeadersInit = {};

    // Only set Content-Type for non-FormData requests
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Check if a request URL is for auth endpoints (skip refresh for these)
   */
  private isAuthRequest(path: string): boolean {
    return path.includes('/auth/login') ||
           path.includes('/auth/refresh') ||
           path.includes('/auth/register');
  }

  /**
   * Check if response contains an INVALID_TOKEN error
   */
  private isInvalidTokenError(response: Response, data?: any): boolean {
    // Check status code first
    if (response.status === 401) {
      return true;
    }
    // Check response body for INVALID_TOKEN code
    if (data?.error?.code === 'INVALID_TOKEN') {
      return true;
    }
    if (data?.error?.code === 'INVALID_CREDENTIALS') {
      return true;
    }
    return false;
  }

  /**
   * Handle authentication failure - clear tokens and trigger redirect
   */
  private handleAuthFailure(): void {
    // Clear tokens
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');

    // Trigger the auth error callback if set
    if (onAuthErrorCallback) {
      onAuthErrorCallback();
    }
  }

  /**
   * Refresh access token using refresh token
   * Uses a promise cache to prevent concurrent refresh requests
   */
  private async refreshAccessToken(): Promise<boolean> {
    // Return existing in-flight refresh promise if available
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create new refresh promise
    this.refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;

      try {
        const response = await fetch(buildApiUrl('/api/auth/refresh', this.baseUrl), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (data.success && data.data?.accessToken) {
          localStorage.setItem('auth_token', data.data.accessToken);
          // Also update refresh token if provided
          if (data.data.refreshToken) {
            localStorage.setItem('refresh_token', data.data.refreshToken);
          }
          return true;
        }
      } catch {
        // Refresh failed
      }
      return false;
    })();

    // Clear the promise cache when complete (success or failure)
    this.refreshPromise.finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  /**
   * Handle API response and throw formatted errors
   */
  private async handleResponse<T>(
    response: Response,
    path: string,
    config?: ApiRequestConfig & { originalBody?: string | FormData }
  ): Promise<T> {
    // Clone response for potential re-reading
    const clonedResponse = response.clone();

    // Try to parse response body for error checking
    let responseData: any = null;
    try {
      responseData = await clonedResponse.json();
    } catch {
      // Response is not JSON, continue with normal flow
    }

    // Check for auth errors (401 or INVALID_TOKEN code)
    if (this.isInvalidTokenError(response, responseData) && !this.isAuthRequest(path)) {
      // Try to refresh token
      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        // Retry original request
        const url = this.buildUrl(path, config?.params);
        const isFormData = config?.originalBody instanceof FormData;
        const retryResponse = await fetch(url, {
          method: config?.method || 'GET',
          headers: this.buildHeaders(isFormData),
          body: config?.originalBody,
        });

        if (retryResponse.ok) {
          const json = await retryResponse.json();
          // Unwrap the data field if it exists
          if (config?.unwrapData !== false && json && typeof json === 'object' && 'data' in json) {
            return json.data as T;
          }
          return json as T;
        }
      }

      // Refresh failed, handle auth failure
      this.handleAuthFailure();

      throw new ApiErrorClass({
        message: 'Session expired. Please log in again.',
        statusCode: 401,
        code: 'INVALID_TOKEN',
      });
    }

    if (!response.ok) {
      let errorData: ApiError = {
        message: response.statusText || 'An error occurred',
        statusCode: response.status,
      };

      if (responseData) {
        errorData = { ...errorData, ...responseData.error };
      }

      throw new ApiErrorClass(errorData);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const json = await response.json();

    // API responses are wrapped in { success: true, data: ... }
    // Unwrap the data field if it exists
    if (config?.unwrapData !== false && json && typeof json === 'object' && 'data' in json) {
      return json.data as T;
    }

    return json as T;
  }

  /**
   * Perform GET request
   */
  async get<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.buildHeaders(),
      ...config,
    });
    return this.handleResponse<T>(response, path, config);
  }

  /**
   * Perform POST request
   */
  async post<T>(path: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const body = JSON.stringify(data);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(),
      body,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'POST', originalBody: body });
  }

  /**
   * Perform POST request with FormData (for file uploads)
   */
  async postFormData<T>(path: string, formData: FormData, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(true), // Skip Content-Type for FormData
      body: formData,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'POST', originalBody: formData });
  }

  /**
   * Perform PUT request
   */
  async put<T>(path: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const body = JSON.stringify(data);
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.buildHeaders(),
      body,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'PUT', originalBody: body });
  }

  /**
   * Perform PUT request with FormData (for file uploads)
   */
  async putFormData<T>(path: string, formData: FormData, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.buildHeaders(true), // Skip Content-Type for FormData
      body: formData,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'PUT', originalBody: formData });
  }

  /**
   * Perform PATCH request
   */
  async patch<T>(path: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const body = JSON.stringify(data);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.buildHeaders(),
      body,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'PATCH', originalBody: body });
  }

  /**
   * Perform PATCH request with FormData (for file uploads)
   */
  async patchFormData<T>(path: string, formData: FormData, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.buildHeaders(true),
      body: formData,
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'PATCH', originalBody: formData });
  }

  /**
   * Perform DELETE request
   */
  async delete<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.buildHeaders(),
      ...config,
    });
    return this.handleResponse<T>(response, path, { ...config, method: 'DELETE' });
  }
}

/**
 * Custom Error class for API errors
 */
class ApiErrorClass extends Error implements ApiError {
  statusCode: number;
  code?: string;
  details?: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiError';
    this.statusCode = error.statusCode;
    this.code = error.code;
    this.details = error.details;
  }
}

/**
 * Export singleton instance
 */
export const apiClient = new ApiClient();

/**
 * Export types
 */
export type { ApiRequestConfig, ApiError };
export { ApiErrorClass };
